import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "../panel/Icon";
import { formatPrice, menuUrl, whatsappNumber } from "../../menu.js";
import WhatsAppLogo from "./WhatsAppLogo";
import { trackOrder } from "../../stats.js";
import { orderNumber, saveOrder } from "../../orders.js";
import { clearPendingPayment, createMpPayment, readPendingPayment, savePendingPayment, takesMpPayments, verifyMpPayment } from "../../mercadoPago.js";
import MercadoPagoLogo from "./MercadoPagoLogo";
import { uiTexts } from "../../i18n.js";
import { deliveryCostOf } from "../../businessValidation.js";

const storageKey = (usuarioEmpresa) => "miqrapp-pedido-" + usuarioEmpresa;

const readSaved = (usuarioEmpresa) => {
    try {
        const saved = JSON.parse(localStorage.getItem(storageKey(usuarioEmpresa)));
        return saved && typeof saved === "object" ? saved : {};
    } catch (error) {
        return {};
    }
}

// Carrito del menú público: cantidades por id de producto, guardadas en el navegador
// para que no se pierdan si el cliente recarga. Precios y nombres salen siempre de los
// productos actuales, así que un producto borrado o agotado sale solo del pedido.
// `onAdd` avisa cada vez que se suma un producto (para las estadísticas).
export const useCart = (usuarioEmpresa, products, onAdd) => {

    const [qty, setQty] = useState(() => readSaved(usuarioEmpresa));

    useEffect(() => {
        try {
            localStorage.setItem(storageKey(usuarioEmpresa), JSON.stringify(qty));
        } catch (error) {
            // Sin almacenamiento (modo incógnito, por ejemplo) el carrito igual funciona.
        }
    }, [usuarioEmpresa, qty]);

    const lines = useMemo(() => (products || [])
        .filter((p) => qty[p.id] > 0 && !p.agotado)
        .map((p) => ({ product: p, qty: qty[p.id], subtotal: Number(p.price) * qty[p.id] })),
    [products, qty]);

    const count = lines.reduce((n, l) => n + l.qty, 0);
    // Con algún precio que no es un número no se puede sumar: el total queda a confirmar.
    const total = lines.every((l) => Number.isFinite(l.subtotal)) ? lines.reduce((n, l) => n + l.subtotal, 0) : null;

    const change = (id, delta) => setQty((q) => {
        const next = { ...q, [id]: Math.max(0, Math.min(99, (q[id] || 0) + delta)) };
        if (!next[id]) delete next[id];
        return next;
    });

    return {
        lines, count, total,
        qtyOf: (id) => lines.find((l) => l.product.id === id)?.qty || 0,
        add: (id) => {
            change(id, 1);
            if (onAdd) onAdd(id);
        },
        remove: (id) => change(id, -1),
        clear: () => setQty({})
    };
}

const DELIVERY = { retiro: "Retiro en el local", envio: "Envío a domicilio" };

// Cuentas del pedido según la entrega elegida y el costo de envío del negocio (`costoEnvio`):
// - envio: lo que se suma por el envío (número), o null si no corresponde o es a confirmar.
// - pendiente: eligió envío y el negocio lo confirma por WhatsApp (el total no lo incluye).
// - total: productos + envío, o null si algún precio no es un número.
export const orderTotals = (business, cart, entrega) => {
    const cost = deliveryCostOf(business);
    const envio = entrega === "envio" ? cost : null;
    return {
        cost,
        envio,
        pendiente: entrega === "envio" && cost === null,
        productos: cart.total,
        total: cart.total === null ? null : cart.total + (envio || 0)
    };
}

const deliveryLabel = (cost) => cost === null ? "a confirmar" : cost === 0 ? "gratis" : formatPrice(cost);

// Las líneas del total del mensaje: con envío a domicilio se detalla el costo.
const totalLines = ({ envio, pendiente, productos, total }) => {
    if (pendiente) return [`Envío: a confirmar`, productos === null ? "*Total: a confirmar*" : `*Total sin envío: ${formatPrice(productos)}*`];
    if (envio === null) return [total === null ? "*Total: a confirmar*" : `*Total: ${formatPrice(total)}*`];
    return [
        ...(envio > 0 && productos !== null ? [`Productos: ${formatPrice(productos)}`] : []),
        `Envío: ${deliveryLabel(envio)}`,
        total === null ? "*Total: a confirmar*" : `*Total: ${formatPrice(total)}*`
    ];
}

// Lugar del estado del pago en el mensaje de un pedido con Mercado Pago: se completa
// cuando el cliente vuelve de pagar (paymentLine).
export const PAYMENT_SLOT = "%PAGO%";

export const paymentLine = (estado) =>
    estado === "pagado" ? "Pago: ✅ Pagado con Mercado Pago" :
    estado === "pendiente" ? "Pago: Mercado Pago (en proceso)" :
    "Pago: a coordinar (el pago con Mercado Pago no se completó)";

// Texto del pedido que le llega al negocio. Los *asteriscos* son negrita en WhatsApp.
// Con `numero` (pedidos con Mercado Pago, que se guardan antes de mandarlos) va el número
// del pedido y el lugar del estado del pago.
export const orderMessage = (business, cart, details, numero = null) => {
    const items = cart.lines.map((l) => {
        const each = l.qty > 1 ? ` (${formatPrice(l.product.price)} c/u)` : "";
        const sub = Number.isFinite(l.subtotal) ? formatPrice(l.subtotal) : formatPrice(l.product.price);
        return `• ${l.qty} x ${l.product.name}${each}: ${sub}`;
    });

    const extra = [
        numero && `Pedido: ${orderNumber(numero)}`,
        numero && PAYMENT_SLOT,
        details.nombre.trim() && `Nombre: ${details.nombre.trim()}`,
        details.entrega && `Entrega: ${DELIVERY[details.entrega]}`,
        details.entrega === "envio" && details.direccion.trim() && `Dirección: ${details.direccion.trim()}`,
        details.nota.trim() && `Aclaraciones: ${details.nota.trim()}`
    ].filter(Boolean);

    return [
        `¡Hola ${business.nombreEmpresa}! Quiero hacer este pedido:`,
        "",
        ...items,
        "",
        ...totalLines(orderTotals(business, cart, details.entrega)),
        ...(extra.length ? ["", ...extra] : []),
        "",
        `Pedido hecho desde ${menuUrl(business.usuarioEmpresa).replace(/^https:\/\//, "")}`
    ].join("\n");
}

export const whatsappUrl = (business, message) =>
    "https://wa.me/" + whatsappNumber(business.telefono) + "?text=" + encodeURIComponent(message);

export const orderUrl = (business, cart, details) => whatsappUrl(business, orderMessage(business, cart, details));

// En el celular abre la app de WhatsApp; si el navegador bloquea la pestaña nueva, va directo.
const openWhatsapp = (url) => {
    const opened = window.open(url, "_blank");
    if (opened) opened.opener = null;
    else window.location.href = url;
}

// Botones para sumar y restar un producto. Con cantidad 0 es un solo botón "+".
export const QtyStepper = ({ name, qty, onAdd, onRemove, className = "", lang = "es" }) => {
    const t = uiTexts(lang);
    if (!qty) {
        return (
            <button type="button" className={"lp-menu-qty lp-menu-qty-add " + className} onClick={onAdd} aria-label={t.qtyAdd(name)}>
                <Icon name="plus" size={20} strokeWidth={2.6}></Icon>
            </button>
        )
    }

  return (
    <span className={"lp-menu-qty " + className} role="group" aria-label={t.qtyGroup(name)}>
        <button type="button" onClick={onRemove} aria-label={qty === 1 ? t.qtyRemove(name) : t.qtyLess(name)}>
            <Icon name={qty === 1 ? "trash" : "minus"} size={qty === 1 ? 16 : 18} strokeWidth={2.4}></Icon>
        </button>
        <span aria-live="polite">{qty}</span>
        <button type="button" onClick={onAdd} aria-label={t.qtyMore(name)} disabled={qty >= 99}>
            <Icon name="plus" size={18} strokeWidth={2.4}></Icon>
        </button>
    </span>
  )
}

// Barra fija abajo con el resumen del pedido; abre el carrito.
export const CartBar = ({ cart, onOpen, lang = "es" }) => {
  return (
    <div className={"lp-menu-cartbar" + (cart.count ? " lp-menu-cartbar-on" : "")} aria-hidden={!cart.count}>
        <button type="button" className="lp-menu-cartbar-btn" onClick={onOpen} tabIndex={cart.count ? 0 : -1}>
            <span className="lp-menu-cartbar-count">{cart.count}</span>
            <span className="lp-menu-cartbar-label">{uiTexts(lang).viewOrder}</span>
            <strong>{cart.total === null ? "" : formatPrice(cart.total)}</strong>
        </button>
    </div>
  )
}

const emptyDetails = { nombre: "", entrega: "", direccion: "", nota: "" };

// Contenido del carrito: productos, datos opcionales del cliente y envío por WhatsApp.
// Con el menú traducido (`lang`, `tr`) la pantalla sale en ese idioma, pero el mensaje
// que le llega al negocio sigue en español y con los nombres originales.
export const CartContent = ({ business, cart, onClose, lang = "es", tr = (text) => text }) => {

    const t = uiTexts(lang);
    const [details, setDetails] = useState(emptyDetails);
    const [sentUrl, setSentUrl] = useState(null);

    const set = (name) => (e) => setDetails((d) => ({ ...d, [name]: e.target.value }));
    const totals = orderTotals(business, cart, details.entrega);

    // Se puede pagar online si el negocio conectó Mercado Pago y el total está completo
    // (con el envío "a confirmar" o algún precio sin número no se sabe cuánto cobrar).
    const canPayMp = takesMpPayments(business) && totals.total !== null && totals.total > 0 && !totals.pendiente;
    const [paying, setPaying] = useState(false);
    const [payError, setPayError] = useState(null);
    // Si el pedido ya se guardó y falló Mercado Pago, al reintentar se usa el mismo número.
    const [mpOrder, setMpOrder] = useState(null);
    const viaMp = useRef(false);

    const sendWhatsapp = () => {
        const url = orderUrl(business, cart, details);
        openWhatsapp(url);
        setSentUrl(url);
        trackOrder(business, cart.lines, cart.total, details.entrega);
        // Queda en el panel del negocio (Mi negocio → Pedidos). Si falla, el pedido igual salió por WhatsApp.
        saveOrder(business, cart, details, totals.envio).catch(console.error);
        cart.clear();
    }

    // Pagar con Mercado Pago: se guarda el pedido, n8n arma el cobro con los precios del
    // menú y el cliente va a Mercado Pago. Al volver (?pago=N) ve el estado y avisa por WhatsApp.
    const payMp = async () => {
        setPaying(true);
        setPayError(null);
        try {
            let numero = mpOrder;
            if (!numero) {
                numero = await saveOrder(business, cart, details, totals.envio, "mp");
                setMpOrder(numero);
                trackOrder(business, cart.lines, cart.total, details.entrega);
            }
            const result = await createMpPayment(business.usuarioEmpresa, numero);
            if (!result.initPoint) throw new Error(t.payFailed);
            savePendingPayment(business.usuarioEmpresa, { numero, message: orderMessage(business, cart, details, numero), initPoint: result.initPoint });
            cart.clear();
            window.location.href = result.initPoint;
        } catch (error) {
            console.error(error);
            setPayError(t.payFailed);
            setPaying(false);
        }
    }

    const send = (e) => {
        e.preventDefault();
        if (paying) return;
        if (canPayMp && viaMp.current) payMp();
        else sendWhatsapp();
    }

    if (sentUrl) {
        return (
            <div className="lp-menu-cart lp-menu-cart-done">
                <span className="lp-menu-cart-check" aria-hidden="true"><Icon name="check" size={28} strokeWidth={2.6}></Icon></span>
                <p><strong>{t.doneTitle}</strong></p>
                <p className="lp-menu-muted">{t.doneText(business.nombreEmpresa)}</p>
                <div className="lp-menu-cart-actions">
                    <a className="lp-menu-btn" href={sentUrl} target="_blank" rel="noreferrer">{t.openWhatsappAgain}</a>
                    <button type="button" className="lp-menu-btn lp-menu-btn-primary" onClick={onClose}>{t.backToMenu}</button>
                </div>
            </div>
        )
    }

    if (!cart.count) {
        return (
            <div className="lp-menu-cart lp-menu-cart-done">
                <p className="lp-menu-muted">{t.emptyCart}</p>
                <div className="lp-menu-cart-actions">
                    <button type="button" className="lp-menu-btn lp-menu-btn-primary" onClick={onClose}>{t.backToMenu}</button>
                </div>
            </div>
        )
    }

  return (
    <form className="lp-menu-cart lp-menu-cart-form" onSubmit={send}>
        <div className="lp-menu-cart-scroll">
            <ul className="lp-menu-cart-lines">
                {cart.lines.map(({ product, qty, subtotal }) => (
                    <li key={product.id}>
                        <span className="lp-menu-cart-name">
                            {tr(product.name)}
                            <span className="lp-menu-muted">{formatPrice(product.price)} {t.each}</span>
                        </span>
                        <QtyStepper lang={lang} name={tr(product.name)} qty={qty} onAdd={() => cart.add(product.id)} onRemove={() => cart.remove(product.id)}></QtyStepper>
                        <strong className="lp-menu-cart-sub">{Number.isFinite(subtotal) ? formatPrice(subtotal) : ""}</strong>
                    </li>
                ))}
            </ul>

            <fieldset className="lp-menu-cart-fields">
                <legend className="lp-menu-sr">{t.yourData}</legend>
                <label className="lp-menu-field">
                    <span>{t.yourName}</span>
                    <input type="text" autoComplete="given-name" maxLength={60} value={details.nombre} onChange={set("nombre")}/>
                </label>

                <div className="lp-menu-field">
                    <span id="cart-entrega">{t.delivery}</span>
                    <div className="lp-menu-chips lp-menu-cart-chips" role="radiogroup" aria-labelledby="cart-entrega">
                        {Object.keys(DELIVERY).map((key) => (
                            <button key={key} type="button" role="radio" aria-checked={details.entrega === key}
                                className={details.entrega === key ? "lp-menu-chip lp-menu-chip-on" : "lp-menu-chip"}
                                onClick={() => setDetails((d) => ({ ...d, entrega: d.entrega === key ? "" : key }))}>
                                {key === "envio" ? t.shipping : t.pickup}{key === "envio" && totals.cost !== null && <span className="lp-menu-chip-extra">{totals.cost ? "+" + formatPrice(totals.cost) : t.free}</span>}
                            </button>
                        ))}
                    </div>
                </div>

                {details.entrega === "envio" &&
                    <label className="lp-menu-field">
                        <span>{t.address}</span>
                        <input type="text" autoComplete="street-address" required maxLength={120} value={details.direccion} onChange={set("direccion")}/>
                    </label>
                }

                <label className="lp-menu-field">
                    <span>{t.notes}</span>
                    <textarea rows="2" maxLength={300} placeholder={t.notesPlaceholder} value={details.nota} onChange={set("nota")}></textarea>
                </label>
            </fieldset>
        </div>

        {/* Fijo abajo: con muchos productos solo se desliza la lista de arriba. */}
        <div className="lp-menu-cart-foot">
            {details.entrega === "envio" &&
                <div className="lp-menu-cart-delivery">
                    <span>{t.shipping}</span>
                    <span>{totals.envio === null ? t.toConfirm : totals.envio ? formatPrice(totals.envio) : t.freeCap}</span>
                </div>
            }
            <div className="lp-menu-cart-total">
                <span>{t.total} <span className="lp-menu-muted">({t.products(cart.count)})</span></span>
                <strong>{totals.total === null ? t.toConfirm : formatPrice(totals.total)}</strong>
            </div>
            {/* Si el negocio no cargó el costo del envío, que el cliente sepa que no está sumado. */}
            {totals.cost === null && details.entrega !== "retiro" &&
                <p className="lp-menu-cart-nodelivery">
                    {details.entrega === "envio" ? t.noShippingConfirm(business.nombreEmpresa) : t.noShipping}
                </p>
            }
            {canPayMp ?
                <>
                    {payError && <p className="lp-menu-cart-payerror" role="alert">{payError}</p>}
                    <button type="submit" className="lp-menu-btn lp-menu-cart-send lp-menu-cart-mp" disabled={paying} onClick={() => { viaMp.current = true; }}>
                        <MercadoPagoLogo size={24}></MercadoPagoLogo> {paying ? t.paying : t.payMp}
                    </button>
                    <button type="submit" className="lp-menu-btn lp-menu-cart-send lp-menu-cart-later" disabled={paying} onClick={() => { viaMp.current = false; }}>
                        <WhatsAppLogo size={20}></WhatsAppLogo> {t.sendOrderPayLater}
                    </button>
                    <p className="lp-menu-cart-note lp-menu-muted">{t.payNote(business.nombreEmpresa)}</p>
                </> :
                <>
                    <button type="submit" className="lp-menu-btn lp-menu-btn-primary lp-menu-cart-send">
                        <WhatsAppLogo size={22}></WhatsAppLogo> {t.sendOrder}
                    </button>
                    <p className="lp-menu-cart-note lp-menu-muted">{t.sendNote(business.nombreEmpresa)}</p>
                </>
            }
        </div>
    </form>
  )
}

// Al volver de Mercado Pago (?pago=N): se le pregunta a n8n cómo quedó el pago y el
// cliente le avisa al negocio por WhatsApp. Si pagó en otro dispositivo (sin el pedido
// guardado en este navegador) solo se ve el estado.
export const PaymentReturn = ({ business, numero, onClose, lang = "es" }) => {

    const t = uiTexts(lang);
    const [pending] = useState(() => {
        const saved = readPendingPayment(business.usuarioEmpresa);
        return saved && saved.numero === numero ? saved : null;
    });
    const [estado, setEstado] = useState(null);
    const [failed, setFailed] = useState(false);
    const [retrying, setRetrying] = useState(false);

    const check = useCallback(async () => {
        setFailed(false);
        setEstado(null);
        try {
            // Mercado Pago a veces tarda unos segundos en dar el pago por aprobado.
            for (let attempt = 0; attempt < 3; attempt++) {
                const result = await verifyMpPayment(business.usuarioEmpresa, numero);
                if (result.estado === "pagado" || attempt === 2) {
                    setEstado(result.estado || "sin-pago");
                    return;
                }
                await new Promise((resolve) => setTimeout(resolve, 2500));
            }
        } catch (error) {
            console.error(error);
            setFailed(true);
        }
    }, [business.usuarioEmpresa, numero]);

    useEffect(() => {
        check();
    }, [check]);

    const notify = () => {
        openWhatsapp(whatsappUrl(business, pending.message.replace(PAYMENT_SLOT, paymentLine(estado))));
        clearPendingPayment(business.usuarioEmpresa);
    }

    const retry = async () => {
        setRetrying(true);
        try {
            const result = await createMpPayment(business.usuarioEmpresa, numero);
            if (result.pagado) {
                setEstado("pagado");
                setRetrying(false);
                return;
            }
            if (!result.initPoint) throw new Error(t.payFailed);
            window.location.href = result.initPoint;
        } catch (error) {
            console.error(error);
            setRetrying(false);
            setFailed(true);
        }
    }

    const close = () => {
        if (estado === "pagado") clearPendingPayment(business.usuarioEmpresa);
        onClose();
    }

    if (failed || !estado) {
        return (
            <div className="lp-menu-cart lp-menu-cart-done" aria-live="polite">
                <p><strong>{failed ? t.payCheckFailed : t.payChecking}</strong></p>
                <p className="lp-menu-muted">{t.orderLabel(orderNumber(numero))}</p>
                {failed &&
                    <div className="lp-menu-cart-actions">
                        <button type="button" className="lp-menu-btn lp-menu-btn-primary" onClick={check}>{t.retry}</button>
                        <button type="button" className="lp-menu-btn" onClick={close}>{t.backToMenu}</button>
                    </div>
                }
            </div>
        )
    }

    const ok = estado === "pagado" || estado === "pendiente";

  return (
    <div className="lp-menu-cart lp-menu-cart-done" aria-live="polite">
        {ok && <span className="lp-menu-cart-check" aria-hidden="true"><Icon name="check" size={28} strokeWidth={2.6}></Icon></span>}
        <p><strong>{estado === "pagado" ? t.payApproved : estado === "pendiente" ? t.payPending : t.payNotDone}</strong></p>
        <p className="lp-menu-muted">
            {t.orderLabel(orderNumber(numero))}.{" "}
            {estado === "pagado" ? (pending ? t.payApprovedText(business.nombreEmpresa) : t.payApprovedNoMsg(business.nombreEmpresa)) :
                estado === "pendiente" ? t.payPendingText(business.nombreEmpresa) : t.payNotDoneText}
        </p>
        <div className="lp-menu-cart-actions">
            {!ok &&
                <button type="button" className="lp-menu-btn lp-menu-cart-mp" disabled={retrying} onClick={retry}>
                    <MercadoPagoLogo size={22}></MercadoPagoLogo> {retrying ? t.paying : t.payRetry}
                </button>
            }
            {pending &&
                <button type="button" className={ok ? "lp-menu-btn lp-menu-btn-primary" : "lp-menu-btn"} onClick={notify}>
                    <WhatsAppLogo size={20}></WhatsAppLogo> {ok ? t.notifyWhatsapp : t.sendOrderPayLater}
                </button>
            }
            <button type="button" className="lp-menu-btn" onClick={close}>{t.backToMenu}</button>
        </div>
    </div>
  )
}
